import React, { useEffect, useState } from 'react';

import { styled } from '@material-ui/core/styles';

import { SdmTypography, RHFUploadMultiFile } from 'components';

import {
  closestCenter,
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import SortableSliderImg from '../additionalComponents/SortableSliderImg';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import MultiFileCropperDialog from '../dialogs/MultiFileCropperDialog';

const variants = {
  simpleFile: {
    display: 'flex',
    justifyContent: 'space-between',
    '& div': {
      margin: 0,
    },
  },
  multipleFiles: {
    '& > div': {
      display: 'flex',
      flexWrap: 'wrap',
    },
    display: 'flex',
    flexDirection: 'column',
  },
};

const CustomImageContainer = styled('div')(({ variant = 'simpleFile' }) => ({
  ...variants[variant],
}));

const MultipleDropZonePreviewImage = ({
  control,
  error,
  name,
  accept,
  watch,
  setValue,
  specs,
  handleChange,
  defaultValue = [],
  handleImgClick,
  onRemoved,
  parentComponentIdx,
  tooltipText,
  disabled = false,
  maxResolution,
  minResolution,
  setCustomErrors,
}) => {
  const intl = useIntl();
  const i18n = translations.productPage(intl);
  const wFile = watch(name);
  const wPositions = watch(
    `additionalContents[${parentComponentIdx}].filePositions`
  );
  const [clicked, setClicked] = useState(-1);
  const [cropperDialog, setCropperDialog] = useState({
    isOpen: false,
    imgs: [],
  });

  const cropBeforeDrop = (files) => {
    setCropperDialog({
      isOpen: true,
      i18n: i18n.info.cropper,
      imgs: files,
    });
  };

  const handleMinHeightError = ({ files, minHeight }) => {
    var _URL = window.URL || window.webkitURL;
    Promise.all(
      files.map(
        (file) =>
          new Promise((resolve, reject) => {
            const img = new Image();
            var objectUrl = _URL.createObjectURL(file);
            img.onload = function () {
              const fileHeight = img.height;
              const hasHeightError = fileHeight < minHeight;
              if (hasHeightError) {
                return setCustomErrors({
                  i18n: {
                    title: i18n.errors.width.title,
                    body: `${i18n.errors.width.minHeight} ${minHeight}px`,
                  },
                });
              }
              _URL.revokeObjectURL(objectUrl);
              resolve(file);
            };
            img.onerror = reject;
            img.src = objectUrl;
          })
      )
    ).then((files) => cropBeforeDrop(files));
  };
  /**
   * @dnd-kit Logics
   */
  const [sortedItems, setSortedItems] = useState([]);

  // Effects
  useEffect(() => {
    if (wFile && wFile.length !== sortedItems?.length) {
      const parsedWPos = wFile
        .map((img, index) => ({
          ...img,
          position: wPositions[index],
        }))
        .sort((a, b) => a.position - b.position);
      setSortedItems(parsedWPos.map((f) => f.preview.split('/').pop()));
      setClicked(parsedWPos?.length - 1);
      handleImgClick(parsedWPos?.length - 1);
    }
  }, [wFile, wPositions]);

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const onRemove = (index) => {
    const newFiles = wFile?.filter((value, i) => index !== i);
    onRemoved(index);
    setValue(name, newFiles);
  };
  const handleDragStart = (event) => {
    const targetId = event.active.id;
    const fileIdx = wFile.findIndex(
      (f) => f.preview.split('/').pop() === targetId
    );
    setClicked(fileIdx);
    handleImgClick(fileIdx);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!active?.id || !over?.id) return;
    if (active.id !== over.id) {
      const activeIdx = sortedItems.indexOf(active.id);
      const overIdx = sortedItems.indexOf(over.id);
      const array = arrayMove(sortedItems, activeIdx, overIdx);
      // Devo aggiornare il riferimento alle position anche nei Fields
      const orderedByPos = array.map((id) =>
        wFile.findIndex((f) => f.preview.split('/').pop() === id)
      );

      setValue(
        `additionalContents[${parentComponentIdx}].filePositions`,
        orderedByPos
      );
      setSortedItems(array);
    }
  };

  const renderSortableElement = ({ id }) => {
    const file = wFile.find((f) => f.preview.split('/').pop() === id);
    const fileIdx = wFile.findIndex((f) => f.preview.split('/').pop() === id);
    return (
      file && (
        <SortableSliderImg
          key={file.preview.split('/').pop()}
          preview={file.preview}
          index={fileIdx}
          onRemove={onRemove}
          clicked={clicked}
          id={file.preview.split('/').pop()}
          tooltipText={tooltipText}
          hideDeleteBtn={disabled}
        />
      )
    );
  };

  const handleCroppedUpload = (files) => {
    files.map((file) => {
      Object.assign(file, {
        path: file.name,
      });
    });
    if (files?.length && files.every((f) => !!f.name && !!f.type)) {
      setCropperDialog({ isOpen: false });
      handleChange(files);
    }
  };

  // Renders
  const renderCropperDialog = () => {
    if (!cropperDialog.isOpen || !cropperDialog.imgs?.length) return null;
    const fileMaxSize = maxResolution.split('x');
    const fileMinSize = minResolution.split('x');
    const maxWidth = parseInt(fileMaxSize?.[0]);
    const maxHeight = parseInt(fileMaxSize?.[1]);
    const minWidth = parseInt(fileMinSize?.[0]);
    const minHeight = parseInt(fileMinSize?.[1]);

    return (
      <MultiFileCropperDialog
        onClose={() => {
          setCropperDialog({ isOpen: false });
        }}
        i18n={{
          ...cropperDialog.i18n,
          agree: 'Ok',
        }}
        onAccept={handleCroppedUpload}
        imgs={cropperDialog.imgs}
        maxHeight={maxHeight}
        minHeight={minHeight}
        minWidth={minWidth}
        maxWidth={maxWidth}
      />
    );
  };

  return (
    <div>
      <RHFUploadMultiFile
        hideUploadedImages
        name={name}
        control={control}
        error={error}
        accept={accept}
        defaultValue={defaultValue}
        onDrop={(files) => {
          const fileMinSize = minResolution.split('x');
          const minHeight = parseInt(fileMinSize?.[1]);
          return handleMinHeightError({
            files,
            minHeight,
          });
        }}
        onRemove={onRemove}
        isMultiple={true}
        dropZoneStyle={{ width: '553px', height: '81px' }}
        disabled={disabled}
      />
      <div style={{ marginBottom: '15px', marginTop: '15px' }}>
        {specs.map((spec) => (
          <SdmTypography
            variant={'h5Regular'}
            style={{ margin: 0, fontStyle: 'italic' }}
            key={spec}
          >
            {spec}
          </SdmTypography>
        ))}
      </div>
      {renderCropperDialog()}
      {wFile?.length ? (
        <>
          <CustomImageContainer variant={'multipleFiles'}>
            <div>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                onDragStart={handleDragStart}
              >
                <SortableContext
                  items={sortedItems}
                  strategy={rectSortingStrategy}
                >
                  {sortedItems.map((id) => renderSortableElement({ id }))}
                </SortableContext>
              </DndContext>
            </div>
          </CustomImageContainer>
        </>
      ) : null}
    </div>
  );
};

export default MultipleDropZonePreviewImage;
